import React, { useState, useRef } from "react";
import "./contentpage.css";
import Question from "../components/Question";
import LandingSection from "./LandingSection";
import FloatingPageNav from "../components/FloatingPageNav";

function Economics41() {

   const contentRef = useRef(null);
         const links = {
        prev: "/economics-40", 
        index: "/index-economics",                 
        next: "/economics-42", 
      };
        const titleText ="The Case for Capital Accumulation";

        const questions = [
  {
    text: "1. What is the common misconception about saving that the chapter addresses?",
    options: [
      { value: "A", label: "A. Savings lead to inflation" },
      {
        value: "B",
        label: "B. Savings reduce economic growth by lowering spending",
      },
      { value: "C", label: "C. Savings have no impact on the economy" },
      {
        value: "D",
        label: "D. Savings automatically increase wages",
      },
    ],
    correct: "B",
  },
  {
    text: "2. According to the text, what actually happens to savings in a healthy economy?",
    options: [
      { value: "A", label: "A. They remain idle in bank vaults" },
      {
        value: "B",
        label: "B. They are used only for government spending",
      },
      {
        value: "C",
        label: "C. They are invested into productive ventures",
      },
      {
        value: "D",
        label: "D. They disappear from the money supply",
      },
    ],
    correct: "C",
  },
  {
    text: "3. How do savings contribute to long-term prosperity?",
    options: [
      {
        value: "A",
        label: "A. By increasing immediate consumer demand",
      },
      {
        value: "B",
        label:
          "B. By funding investments that improve production and efficiency",
      },
      { value: "C", label: "C. By causing prices to rise" },
      { value: "D", label: "D. By reducing imports" },
    ],
    correct: "B",
  },
  {
    text: "4. Which of the following is a direct result of savings being invested?",
    options: [
      { value: "A", label: "A. Shortage of goods" },
      { value: "B", label: "B. Higher unemployment" },
      {
        value: "C",
        label: "C. Development of new technology and job creation",
      },
      {
        value: "D",
        label: "D. Reduction in business competition",
      },
    ],
    correct: "C",
  },
];

  // State to track selected answers for all questions
      const [answers, setAnswers] = useState({});
    
      // Function to update an answer
      const handleAnswerChange = (questionIndex, selectedValue) => {
        setAnswers((prev) => ({ ...prev, [questionIndex]: selectedValue }));
      };
    
      // Compute score
      const score = questions.reduce((total, q, index) => {
        return total + (answers[index] === q.correct ? 1 : 0);
      }, 0);
    

  return (
    <div className="container">
     <LandingSection
              titleText={titleText}
              scrollToRef={contentRef}
              progressValue={41}
              timerMinutes={60}
              currentLevel={2}
              unlockedBadges={3}
            />

      <p className="content" ref={contentRef}>
        Welcome to <b>Day 41</b>! Today, we uncover the often-misunderstood
        power of saving. Many believe that if people save more, the economy will
        suffer from reduced spending. In truth, savings do not vanish, rather
        they are channeled into investments that build factories, develop
        technology, and create jobs. By saving, individuals provide the fuel for
        long-term economic growth, enabling societies to enjoy better products,
        higher productivity, and lasting prosperity.
        <br />
        <br />
        <b>The Misunderstood Virtue of Saving</b>
        <br />
        <br />
        Since the beginning of civilization, the virtue of saving has been
        deeply embedded in human culture. It finds resonance in the teachings of
        wise men, in ancient texts, and in common-sense family values that have
        guided generations. Saving for the future has long been regarded as a
        pillar of personal responsibility, resilience, and foresight. However,
        in recent times, a new economic school of thought has emerged that
        challenges this time-honored virtue. A growing number of inflationists
        argue that saving is not only unnecessary in a modern economy but can
        also be detrimental to society. According to them, saving merely
        benefits the individual saver while depriving the broader economy of
        circulating money. These critics portray savers as hoarders who stash
        away wealth rather than spending it to stimulate economic activity. They
        believe that when people save rather than spend, money is removed from
        the system, leading to reduced consumption, sluggish demand, and slower
        economic growth. In their view, this hoarding justifies inflation as a
        policy tool, which deliberately reduces the purchasing power of money to
        discourage saving and encourage spending. But this perspective is not
        only flawed in its assumptions but also dangerous in its long-term
        implications. What these critics fail to understand is that financially
        prudent individuals do not simply stash their money in drawers or under
        mattresses. Rather, they allocate their savings to productive ventures
        such as businesses, investment instruments, and infrastructure
        development. These savings become the fuel that drives innovation, job
        creation, and capital formation. In fact, savings act as a transfer of
        wealth from the world of consumer goods to the realm of capital goods.
        When a person decides not to buy a spare car or the latest Iphone and
        instead saves that money, the funds are not lost to society. Instead,
        they are channeled into the productive side of the economy, enabling the
        creation of factories, tools, technologies, and startups that raise the
        standard of living for all. Without savings, the production of capital
        goods would come to a halt, and so would technological progress. If
        everyone were to heed the inflationist call and focus only on spending,
        it would lead to a surge in the demand for consumer goods, pushing their
        prices higher. Meanwhile, the decline in savings would translate to
        fewer investments in machinery, tools, and research. This imbalance
        would eventually result in reduced productivity, supply bottlenecks, and
        economic stagnation. Far from being a social vice, savings are a
        cornerstone of sustainable development.
        <br />
        <br />
        <b>Savings vs. Hoarding</b>
        <br />
        <br />
        Much of the confusion stems from the incorrect definition of savings
        propagated by those who advocate for inflation. To them, savings are
        akin to money being kept idle, as if wealth is merely transferred from
        one individual to another without generating any broader benefit. This
        shallow view completely ignores the dynamic and complex nature of modern
        financial systems. In reality, the instances where money is literally
        hoarded are extremely rare. Such behavior typically surfaces only during
        times of extreme uncertainty, such as war or an economic collapse. In
        such scenarios, individuals naturally seek to safeguard their assets
        until a more stable environment returns. Another situation in which
        people may delay spending is when they anticipate a fall in the prices
        of goods and services. In such cases, consumers may postpone purchases
        in order to buy the same items at a lower cost in the near future.
        However, this temporary deferment should not be misinterpreted as
        harmful hoarding. In fact, it reflects a rational assessment of market
        conditions. Similarly, when people lack confidence in the economy or
        expect reduced profitability in the future, they may avoid investing in
        businesses. But this too is not a failure of savings; it is a reflection
        of poor policy decisions and systemic uncertainty. When the business
        environment is conducive to growth, savers are eager to invest their
        money because of the promise of returns and value creation. Therefore,
        the true cause of idle capital is not the virtue of saving itself, but
        rather the absence of a stable and promising economic framework. It is
        the responsibility of policymakers to foster an environment in which
        savings can be safely and productively deployed. Attacking the savers
        and attempting to forcefully erode the value of their money through
        inflation not only disrespects the prudent but also punishes the very
        behavior that enables economic resilience. Every currency needs a
        foundation of trust and stability, and savings are that foundation. When
        people are confident that their wealth will retain value over time, they
        are far more likely to invest it into longer-term ventures that benefit
        the entire society.
        <br />
        <br />
        <b>The Potential of Capital Accumulation</b>
        <br />
        <br />
        Another deeply flawed argument made by the critics of saving is the
        belief that the economy has a natural ceiling on the amount of capital
        that can be accumulated. According to this line of thinking, once a
        certain amount of capital goods is produced, additional savings no
        longer contribute to growth and therefore should be discouraged. This
        could not be further from the truth. There is virtually no end to the
        ways in which capital can improve the quality of life, raise
        productivity, and reduce the costs of production. Consider the realm of
        durable consumer goods such as cars, furniture, homes, refrigerators, or
        even public libraries. There is no level of availability at which
        everyone’s needs are forever satisfied. As populations grow and
        individual preferences evolve, the demand for these goods constantly
        renews itself. Even if the quantity of these goods were somehow made
        sufficient for all, there would still be an infinite scope for
        qualitative improvement. A bookshelf made of cheap plastic can be
        replaced by a handcrafted wooden cabinet. A two-bedroom house can evolve
        into a smart home with energy-efficient features. The same holds true
        for capital goods used in the manufacturing sector. Tools such as
        knives, plows, axes, or complex machinery like robotic arms, conveyor
        belts, and AI-driven logistics systems can always be upgraded, refined,
        or replaced by more efficient versions. Capital investment either
        creates entirely new classes of consumer goods or dramatically enhances
        the quality and efficiency of existing production processes. There is no
        end to innovation, no point at which humanity can declare that it has
        created enough. Moreover, every improvement in capital goods has a
        multiplying effect. When a new machine reduces the cost of manufacturing
        a product by half, the same capital investment results in greater
        affordability for the consumer, increased profits for the entrepreneur,
        and potentially higher wages for the worker. This cycle of improvement,
        driven by savings and investment, is what propels economies forward.
        When we accept the idea that capital formation has a limit, we trap
        ourselves in a world with no room for progress. We deny the possibility
        of a better tomorrow by attacking the very tool that builds it. In a
        truly free and forward-looking economy, savings are not merely
        tolerated; they are celebrated. They represent deferred gratification in
        exchange for long-term prosperity. The more a society saves and invests,
        the more it grows in terms of material wealth, technological capacity,
        and economic dignity.
        <br />
        <br />
        <b>Summary of Today’s Lesson</b>
        <br />
        <br />
        Saving is not merely a private virtue but a foundation for collective
        prosperity. When individuals save, they make resources available for
        investment in tools, technology, and production, leading to higher
        efficiency, better goods, and improved living standards. The common
        belief that saving harms society by reducing spending is misguided; in
        reality, savings are what fund growth and progress. Far from being a
        drag on the economy, saving is the quiet force that drives innovation,
        job creation, and long-term stability.
        <br />
        <br />
        <b> Coming Up Next</b>
        <br />
        <br />
        In the next lesson, we will explore the concept of time preference and its crucial role in shaping human behavior and economic decisions. While today we focused on savings and the benefits of deferring consumption, tomorrow we will see why the value people place on present versus future consumption varies across individuals and societies. We will examine how time preference influences investment, capital accumulation, and even the stability of civilizations. Understanding this concept will also help us appreciate how money, from gold to modern innovations like Bitcoin, interacts with time preference to encourage long-term planning and economic growth.
      </p>
      <h2>Test your understanding</h2>
       {questions.map((q, index) => (
      <Question
        key={index}
        questionText={q.text}
        options={q.options}
        correctAnswer={q.correct}
        onAnswer={(value) => handleAnswerChange(index, value)} // pass parent handler
      />
    ))}
    
           <div style={{ marginTop: "20px", fontWeight: "bold", fontSize: "1.2em" }}>
            Score: {score} / {questions.length}
          </div>
   
      <FloatingPageNav 
              prevLink={links.prev} 
              indexLink={links.index} 
              nextLink={links.next} 
            />
    </div>
  );
}

export default Economics41;
